import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseScrollBoundaryOptions {
  /** Distance from bottom (px) to consider "at bottom". Default 50. */
  thresholdPx?: number;
}

export interface UseScrollBoundaryResult {
  /** Whether scroll container has scrollable overflow */
  isScrollable: boolean;
  /** Whether scroll position is at/near the bottom */
  isAtBottom: boolean;
  /** Manually trigger a recalculation (e.g., after content height changes) */
  update: () => void;
}

export function useScrollBoundary(
  scrollRef: React.RefObject<HTMLElement | null>,
  options?: UseScrollBoundaryOptions
): UseScrollBoundaryResult {
  const { thresholdPx = 50 } = options ?? {};

  const [state, setState] = useState({ isScrollable: false, isAtBottom: false });
  const [element, setElement] = useState<HTMLElement | null>(null);
  const prevStateRef = useRef(state);

  useEffect(() => {
    const checkRef = () => {
      if (scrollRef.current !== element) {
        setElement(scrollRef.current);
      }
    };
    checkRef();
    const interval = setInterval(checkRef, 100);
    return () => clearInterval(interval);
  }, [scrollRef, element]);

  const calculate = useCallback(() => {
    const el = element;
    if (!el) {
      const newState = { isScrollable: false, isAtBottom: false };
      if (prevStateRef.current.isScrollable !== newState.isScrollable || 
          prevStateRef.current.isAtBottom !== newState.isAtBottom) {
        prevStateRef.current = newState;
        setState(newState);
      }
      return;
    }

    const { scrollHeight, scrollTop, clientHeight } = el;
    const isScrollable = scrollHeight > clientHeight + 1;
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    const isAtBottom = distanceToBottom <= thresholdPx;

    if (prevStateRef.current.isScrollable !== isScrollable || 
        prevStateRef.current.isAtBottom !== isAtBottom) {
      const newState = { isScrollable, isAtBottom };
      prevStateRef.current = newState;
      setState(newState);
    }
  }, [element, thresholdPx]);

  const update = useCallback(() => {
    calculate();
  }, [calculate]);

  useEffect(() => {
    if (!element) return;

    const onScroll = () => {
      calculate();
    };

    element.addEventListener('scroll', onScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      calculate();
    });
    resizeObserver.observe(element);

    const mutationObserver = new MutationObserver(() => {
      calculate();
    });
    mutationObserver.observe(element, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });

    calculate();

    return () => {
      element.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element, calculate]);

  return { isScrollable: state.isScrollable, isAtBottom: state.isAtBottom, update };
}

/**
 * Returns true if user prefers reduced motion.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Returns scroll behavior based on user preference.
 */
export function getScrollBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? 'auto' : 'smooth';
}
